import { z } from 'zod';
/**
 * Inbound webhook payload from the DemocracyCraft Economy API.
 *
 * The core fields now match the confirmed `TransactionItem` schema from the
 * OpenAPI document. The webhook *delivery* payload is still not documented
 * there - only the webhook management endpoints are - so the speculative fields
 * are kept alongside and the schema stays permissive. See
 * docs/api-verification.md.
 *
 * Note what is NOT in a transaction: item code, quantity and unit price. A
 * money movement is money, not a trade line. Item attribution has to come from
 * the memo, or from the ChestShop market endpoints.
 */
export const dcMoneyMovementSchema = z
    .object({
    // --- confirmed TransactionItem fields (from the OpenAPI spec) ---
    /** Ledger posting id - the natural idempotency key. */
    postingId: z.union([z.string(), z.number()]).optional(),
    /** Transaction id; several postings can share one. */
    txnId: z.union([z.string(), z.number()]).optional(),
    /** Decimal STRING. The API is explicit that this is never a JSON number. */
    amount: z.union([z.string(), z.number()]).optional(),
    memo: z.string().nullable().optional(),
    message: z.string().nullable().optional(),
    settledAt: z.union([z.string(), z.number()]).optional(),
    initiatorUuid: z.string().nullable().optional(),
    /** Which plugin produced the movement, e.g. a ChestShop trade. */
    pluginSystem: z.string().nullable().optional(),
    // --- fields the webhook delivery may add ---
    // The delivery payload is NOT in the OpenAPI document (only the webhook
    // management endpoints are), so these remain speculative and optional.
    id: z.union([z.string(), z.number()]).optional(),
    eventId: z.union([z.string(), z.number()]).optional(),
    type: z.string().optional(),
    eventType: z.string().optional(),
    accountId: z.union([z.string(), z.number()]).optional(),
    deliveryId: z.union([z.string(), z.number()]).optional(),
    scope: z.string().optional(),
    // Item detail is absent from TransactionItem. If the webhook payload turns
    // out to carry it, these pick it up; otherwise it is parsed from the memo.
    item: z.string().nullable().optional(),
    itemKey: z.string().nullable().optional(),
    material: z.string().nullable().optional(),
    quantity: z.union([z.number(), z.string()]).optional(),
    unitPrice: z.union([z.number(), z.string()]).optional(),
    // Counterparties, under whichever spelling.
    from: z.string().nullable().optional(),
    to: z.string().nullable().optional(),
    counterparty: z.string().nullable().optional(),
    player: z.string().nullable().optional(),
    ownerName: z.string().nullable().optional(),
    shopId: z.union([z.string(), z.number()]).optional(),
    timestamp: z.union([z.string(), z.number()]).optional(),
    createdAt: z.union([z.string(), z.number()]).optional(),
})
    .passthrough();
/**
 * A real delivery wraps one transaction in an envelope:
 *
 *   { event, deliveryId, subscriptionId, accountId, transaction: {...} }
 *
 * confirmed by the official webhook guide. The batch and bare forms are kept
 * because they cost nothing and the platform used to guess at the shape.
 */
export const dcDeliveryEnvelopeSchema = z
    .object({
    event: z.string().optional(),
    deliveryId: z.union([z.string(), z.number()]).optional(),
    subscriptionId: z.union([z.string(), z.number()]).optional(),
    accountId: z.union([z.string(), z.number()]).optional(),
    transaction: dcMoneyMovementSchema,
})
    .passthrough();
export const dcWebhookBodySchema = z.union([
    dcDeliveryEnvelopeSchema,
    dcMoneyMovementSchema,
    z.object({ events: z.array(dcMoneyMovementSchema) }).passthrough(),
    z.array(dcMoneyMovementSchema),
]);
/* ------------------------------------------------------------------ */
/* Public API request schemas                                          */
/* ------------------------------------------------------------------ */
export const registerSchema = z.object({
    email: z.string().email(),
    password: z.string().min(10, 'Use at least 10 characters'),
    discordHandle: z.string().max(64).optional(),
    /** What they signed up as. Decides which tabs exist; buyer is the default. */
    role: z.enum(['buyer', 'store_owner', 'delivery']).optional(),
});
export const flagItemSchema = z.object({
    itemKey: z.string().min(1).max(120),
    shopId: z.number().int().positive().optional(),
    reason: z.string().max(280).optional(),
});
export const jobSubmissionSchema = z.object({
    submission: z.string().min(1).max(280),
});
export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const createConnectionSchema = z.object({
    accountType: z.enum(['personal', 'firm']),
    dcAccountCode: z.string().min(1).max(64),
    label: z.string().max(64).optional(),
    /** Personal connections must acknowledge the privacy disclosure. */
    privacyAcknowledged: z.boolean().optional(),
});
export const createShopSchema = z.object({
    connectedAccountId: z.string().min(1),
    name: z.string().min(1).max(80),
    city: z.string().max(64).optional(),
    district: z.string().max(64).optional(),
    coords: z.string().max(64).optional(),
    ownerLine: z.string().max(80).optional(),
});
/**
 * Manual `/find` baseline entry. The in-game GUI has no export and automating
 * the command is disallowed, so an operator types the average buy price they
 * saw. Recorded with attribution so stale entries can be aged out.
 */
export const baselineObservationSchema = z.object({
    itemCode: z.string().min(1).max(64),
    averagePrice: z.number().positive(),
    storeCount: z.number().int().positive().max(10_000).optional(),
    district: z.string().max(64).optional(),
    note: z.string().max(280).optional(),
});
export const alertSettingsSchema = z.object({
    undercutThresholdPct: z.number().min(1).max(90).optional(),
    mispricingThresholdPct: z.number().min(1).max(90).optional(),
    discordWebhookUrl: z.string().url().max(500).nullable().optional(),
    channels: z.array(z.enum(['in_app', 'discord', 'email'])).optional(),
});
export const recommendationRequestSchema = z.object({
    targets: z
        .array(z.object({ itemCode: z.string(), targetUnitsPerDay: z.number().positive() }))
        .optional(),
    elasticity: z.number().max(-0.1).min(-5).optional(),
});
//# sourceMappingURL=schemas.js.map